import Attachment20 from '@carbon/icons-react/es/attachment/20';
import CheckmarkOutline20 from '@carbon/icons-react/es/checkmark--outline/20';
import { Editor } from '@ckeditor/ckeditor5-core/src/editor';
import { GridApi } from 'ag-grid-community';
import { Popover } from 'antd';
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { FullScreenHandle } from 'react-full-screen';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { AiFillAppDestination, AiFillRuntimeInput } from 'api/ai-fill/types';
import { ProjectId, StatusUpdateId } from 'api/models';
import {
  CustomerPin,
  PinResourceType,
  PinScopeType,
} from 'api/spotlight/types';
import { StatusUpdateState, StatusUpdate } from 'api/status-updates';
import { confirm } from 'components/atoms/modal';
import { ErrorBoundary } from 'components/molecules/error-boundary';
import { OnUserMention } from 'components/molecules/mention';
import { projectSummaryRenderer } from 'components/molecules/project-summary-renderer';
import { resourceSectionRenderer } from 'components/molecules/resource-section-renderer';
import { toastService } from 'components/molecules/toast';
import { CKCollabEditor } from 'components/organisms/ck-collab-editor';
import { EditorsObject } from 'components/organisms/ck-collab-editor/custom-editor.types';
import {
  saveAllEditors,
  setActiveEditor,
  useDocuments,
} from 'components/organisms/document/document-store';
import { taskSectionRenderer } from 'components/organisms/document/parts/editor/task-section';
import { ToolBtn } from 'components/organisms/document/parts/styled-components';
import { ToolButtonWrapper } from 'components/organisms/document/parts/tool-button-wrapper';
import { Topbar as FooterActionBar } from 'components/organisms/document/parts/topbar';
import { statusUpdateTools } from 'components/organisms/document/parts/topbar/tools';
import {
  checkActiveState,
  checkDefaultEnabled,
} from 'components/organisms/document/parts/topbar/utils';
import { EditorModes, getDocTasks, setActiveDocument } from 'data/documents';
import { useProjectDetail } from 'data/projects';
import { deleteStatusUpdate } from 'data/status-updates/actions/status-updates';
import {
  selectActiveStatusUpdate,
  setActiveStatusUpdate,
  updateStatusUpdate,
} from 'data/status-updates/slice';
import { useGetAiFillsPlugins } from 'hooks/use-get-ai-fills-plugins';
import { useGetAllActiveNativeUsersFromIndexedDb } from 'hooks/use-idb';
import { useMentionsData } from 'hooks/use-mentions-data';
import { useGetSpotlights } from 'hooks/use-spotlight';
import { useTranslation } from 'hooks/use-translation';
import {
  useGetStatusUpdateDetailsQuery,
  useMutationResolveAiFillStatusUpdate,
} from 'queries/status-updates';
import { logger } from 'services';
import { queueMacrotask } from 'utils/macrotask';
import { getProjectStatusUpdatesUrl } from 'utils/urls';
import { INSERT_DOC_TASK_SECTION_COMMAND } from 'vendor/ckeditor/plugins/document-tasks-section/constants';
import { TaskSection } from 'vendor/ckeditor/plugins/document-tasks-section/plugin';
import { ProjectSummarySchema } from 'vendor/ckeditor/plugins/project-summary/constants';
import { ProjectSummary } from 'vendor/ckeditor/plugins/project-summary/plugin';
import { ResourceSectionSchema } from 'vendor/ckeditor/plugins/resource-list-section/constants';
import { ResourceListSection } from 'vendor/ckeditor/plugins/resource-list-section/plugin';
import { VariablesUI } from 'vendor/ckeditor/plugins/variables/variables-ui';
import { AiFillPlaceholderSkeleton } from 'vendor/ckeditor/react-components/ai-fill/ai-fill-placeholder';
import { AIFillSelectorPopover } from 'vendor/ckeditor/react-components/ai-fill/popover';
import { CKMentionPopup } from 'vendor/ckeditor/react-components/mention';
import { CKVariablesPopup } from 'vendor/ckeditor/react-components/variables';
import { SUAttachment } from '../attachments';
import { useActiveStatUpdateCCs } from '../carbon-copy/cc-hooks';
import { EditorHead } from '../editor-head';
import { StatusUpdatePublishModal } from '../publish-modal';
import {
  EditorContentWrapper,
  EditorFooterContentWrapper,
  EditorAttachementsWraper,
  EditorFooterWrapper,
  EditorHeadWrapper,
  ExtraToolsWrapper,
  StatusEditorWrapper,
  ToolsWrapper,
  Wrapper,
  DeleteIcon,
  OutsideEmails,
  MarginSpacer,
} from './editor-styles';
import styles from './editor.module.css';

export const resourceListConfig = (extraContextData, projectId?: number) => ({
  [ResourceSectionSchema]: {
    renderer: (
      {
        resourceSectionId,
        resourceIds,
        resourceSectionType,
        resourceSectionFilterType,
        resourceSectionFilterRange,
      },
      container,
      editor,
    ) => {
      const payload = {
        resourceSectionId,
        resourceIds,
        resourceSectionType,
        resourceSectionFilterType,
        resourceSectionFilterRange,
      };

      return resourceSectionRenderer(
        payload,
        container,
        editor,
        extraContextData,
        projectId,
      );
    },
  },
});

export const projectSummaryconfig = (extraContextData, statusUpdate) => ({
  [ProjectSummarySchema]: {
    renderer: ({ fields, resourceSectionId }, container, editor) => {
      return projectSummaryRenderer(
        { fields, resourceSectionId },
        container,
        editor,
        extraContextData,
        extraContextData?.projectId,
        statusUpdate,
      );
    },
  },
});
export const taskSectionConfig = (extraContextData) => ({
  taskSection: {
    renderer: (resourceSectionId, container, editor) =>
      taskSectionRenderer(
        resourceSectionId,
        container,
        editor,
        extraContextData,
      ),
  },
});

declare global {
  interface Window {
    RLStatusUpdateStore: {
      editor: Editor;
    };
  }
}

export interface CcEmailVal {
  label: string;
  value: string;
}

const TaskSectionToolHandler = ({ editor }) => {
  const handleClick = () => {
    const options = {
      resourceSectionId: Number(new Date()),
    };

    editor?.execute(INSERT_DOC_TASK_SECTION_COMMAND, options);
  };

  return (
    <ToolButtonWrapper onClick={handleClick}>
      <CheckmarkOutline20 />
    </ToolButtonWrapper>
  );
};

const TIMER_TO_CHECK_AUTOREADY = 1000;

export const StatusUpdateEditor = ({
  respMode = false,
  onUserMention,
  editorHeader,
  propProjectId,
  gridApi,
  onTabChange,
  handleFullScreen,
  hasAccessToEdit,
  hasAccessToDelete,
  hasAccessToPublish,
  hasAccessToCreatePrivateUpdate,
  hasAccessToCreatePublicUpdate,
  onPostDelete,
  aiFillRuntimeInput,
  setAiFillRuntimeInput,
  onSpotlightClick,
}: {
  onUserMention: OnUserMention;
  editorHeader?: React.ReactNode;
  propProjectId?: number;
  respMode?: boolean;
  gridApi?: GridApi | null;
  onTabChange: (tab) => void;
  handleFullScreen: FullScreenHandle;
  hasAccessToEdit: boolean;
  hasAccessToDelete: boolean;
  hasAccessToPublish: boolean;
  hasAccessToCreatePrivateUpdate: boolean;
  hasAccessToCreatePublicUpdate: boolean;
  onPostDelete?: VoidFunction;
  aiFillRuntimeInput?: AiFillRuntimeInput | null;
  setAiFillRuntimeInput?: React.Dispatch<
    React.SetStateAction<AiFillRuntimeInput | null>
  >;
  onSpotlightClick?: (pin: CustomerPin) => void;
}): JSX.Element => {
  const { projectId: projectIdStr } = useParams<{ projectId: string }>();
  const projectId =
    propProjectId === undefined ? Number(projectIdStr) : propProjectId;
  const refMentionPopup = useRef<HTMLDivElement | null>(null);
  const toastContainer = useRef<HTMLDivElement | null>(null);
  const [mentionPopupPosition, setMentionPopupPosition] = useState({
    top: 0,
    left: 0,
  });
  const [isAiFillResolving, setIsAiFillResolving] = useState(false);
  const activeStatusUpdate = useSelector(selectActiveStatusUpdate);
  const { data: statusUpdateDetails, refetch } = useGetStatusUpdateDetailsQuery(
    StatusUpdateId(activeStatusUpdate?.statusUpdateId ?? -1),
    { enabled: activeStatusUpdate?.state === StatusUpdateState.Published },
  );

  const dispatch = useDispatch();

  const { mutateAsync } = useMutationResolveAiFillStatusUpdate(
    activeStatusUpdate?.statusUpdateId ?? -1,
    {
      onMutate: () => {
        setIsAiFillResolving(true);
        setAiFillRuntimeInput?.(null);
      },
      onSuccess: async () => {
        const { data: updatedSuData, isLoading } = await refetch();

        if (updatedSuData && !isLoading) {
          dispatch(updateStatusUpdate(updatedSuData));
          dispatch(setActiveStatusUpdate(updatedSuData));
          setIsAiFillResolving(false);
        }
      },
      onError: () => {
        logger.info('Failed to resolve AI Fill');
        toastService({
          message: 'Failed to resolve AI Fill',
          title: 'Error',
        }).error();
        setIsAiFillResolving(false);
      },
    },
  );

  if (
    !isAiFillResolving &&
    aiFillRuntimeInput !== undefined &&
    aiFillRuntimeInput !== null
  ) {
    mutateAsync(aiFillRuntimeInput);
  }

  const users = useGetAllActiveNativeUsersFromIndexedDb();
  const isPublished = activeStatusUpdate?.state === StatusUpdateState.Published;

  const [state, documentStoreDispatch] = useDocuments();

  const aiFillPlugins = useGetAiFillsPlugins();

  const [editor, setEditor] = useState<Editor | null>(null);

  const mentionsData = useMentionsData(ProjectId(projectId));

  const presenceListElementRef = useRef<HTMLDivElement>(null);

  const [currentSU, changeSU] = useState<StatusUpdate | null>(null);

  const { data: project } = useProjectDetail(
    ProjectId(
      propProjectId === undefined ? Number(projectIdStr) : propProjectId,
    ),
    {
      enabled:
        propProjectId !== undefined || !Number.isNaN(Number(projectIdStr)),
    },
  );

  const history = useHistory();

  const deletedToastTitle = useTranslation('status_updates.deleted_msg_title');
  const deletedToastMessage = useTranslation('status_updates.deleted_msg_desc');
  const translatedDeleteConfirmationMessageTitle = useTranslation(
    'status_updates.delete_su_alert_title',
  );
  const translatedDeleteConfirmationMessageContent = useTranslation(
    'status_updates.delete_su_alert_content',
  );

  const addAttachments = useTranslation('toolbar.add_attachment');
  const deleteText = useTranslation('toolbar.delete');

  const ccEmails = useActiveStatUpdateCCs();

  const ccOptions = useMemo(() => {
    return isPublished || !hasAccessToEdit
      ? []
      : ccEmails.map((email) => {
          return { label: email, value: email };
        });
  }, [ccEmails, hasAccessToEdit, isPublished]);

  const [userEmails, setUserEmails] = useState<CcEmailVal[]>(ccOptions);

  useEffect(() => {
    if (!isPublished && hasAccessToEdit) {
      setUserEmails(ccOptions);
    } else {
      setUserEmails([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatusUpdate?.statusUpdateId, isPublished, hasAccessToEdit]);

  const onEditorReady = (allEditors: EditorsObject) => {
    if (allEditors[EditorModes.Public]) {
      setEditor(allEditors[EditorModes.Public]);
      setActiveEditor(documentStoreDispatch, allEditors[EditorModes.Public]);
    }
    saveAllEditors(documentStoreDispatch, allEditors);
  };

  const onEditorFocusChange = (editorT: Editor) => {
    setActiveEditor(documentStoreDispatch, editorT);
  };

  useEffect(() => {
    if (
      activeStatusUpdate?.statusUpdateId !== currentSU?.statusUpdateId ||
      activeStatusUpdate?.state !== currentSU?.state
    ) {
      changeSU(null);
      // So that reloading the CK editor will work.
      queueMacrotask(() => {
        changeSU(activeStatusUpdate);
      });
    }
  }, [activeStatusUpdate, currentSU]);

  useEffect(() => {
    const docId = currentSU?.document?.documentId;

    if (docId) {
      dispatch(getDocTasks({ documentId: docId, projectId }));
      dispatch(
        setActiveDocument({
          documentId: docId,
          projectId,
        }),
      );
    }
  }, [currentSU?.document?.documentId, dispatch, projectId]);
  const editorAutoSaveReady = () => {
    return new Promise<void>((resolve) => {
      let readyStateChecker;
      const isAutoSaveReady = () => editor?.readyToAutoSave === true;
      const setTimerToCheckState = () => {
        readyStateChecker = setTimeout(() => {
          if (isAutoSaveReady()) {
            clearTimeout(readyStateChecker);
            readyStateChecker = null;
            resolve();
          } else {
            setTimerToCheckState();
          }
        }, TIMER_TO_CHECK_AUTOREADY);
      };

      if (editor) {
        if (isAutoSaveReady()) {
          resolve();
        } else {
          setTimerToCheckState();
        }
      } else {
        resolve();
      }
    });
  };

  const saveDocument = async () => {
    if (editor) {
      await editorAutoSaveReady();

      return (editor.config.get('autosave') as any).save(editor);
    }

    return Promise.resolve();
  };

  const onAsyncOperationSuccess = useCallback(({ title, message }) => {
    const service = toastService({
      title,
      message,
    });

    service.success();
  }, []);

  const showDeleteSuccessToast = useCallback(() => {
    onAsyncOperationSuccess({
      title: deletedToastTitle,
      message: deletedToastMessage,
    });

    if (onPostDelete != null) {
      onPostDelete();
    } else {
      history.replace(getProjectStatusUpdatesUrl(ProjectId(projectId)));
    }
  }, [
    onAsyncOperationSuccess,
    deletedToastTitle,
    deletedToastMessage,
    onPostDelete,
    history,
    projectId,
  ]);

  const onDelete = useCallback(() => {
    dispatch(
      deleteStatusUpdate({
        statusUpdate: activeStatusUpdate as StatusUpdate,
        onSuccess: showDeleteSuccessToast,
      }),
    );
  }, [activeStatusUpdate, dispatch, showDeleteSuccessToast]);

  const translatedYesText = useTranslation('delete');
  const translatedNoText = useTranslation('cancel');
  const onConfirm = useCallback(() => {
    return confirm({
      title: translatedDeleteConfirmationMessageTitle,
      content: translatedDeleteConfirmationMessageContent,
      okText: (
        <span data-cy="status_update_delete_confirm">{translatedYesText}</span>
      ),
      okType: 'danger',
      cancelText: <span>{translatedNoText}</span>,
      onOk: onDelete,
      width: 480,
    });
  }, [
    onDelete,
    translatedDeleteConfirmationMessageContent,
    translatedDeleteConfirmationMessageTitle,
    translatedNoText,
    translatedYesText,
  ]);

  // sending a random number to re-call the onMount function that triggers the file manager
  const [trigger, setTrigger] = useState<number | undefined>();
  const [attachmentKey, setAttachmentKey] =
    useState<'attachments' | undefined>(undefined);

  useEffect(() => {
    return () => setAttachmentKey(undefined);
  }, []);

  const fallBack = () => (
    <p>
      <FormattedMessage id="editor.error" />
    </p>
  );

  const openAttachment = () => {
    setTrigger(Date.now());
    setAttachmentKey('attachments');
  };

  const outsideUsers = activeStatusUpdate?.private
    ? userEmails.filter((email) => {
        const isNativeUser = users?.find(
          (user) => user?.emailId === email.value,
        );

        return !isNativeUser;
      })
    : [];

  const outSideEmails = outsideUsers.map(({ value }) => value);

  const renderExtra = useCallback(() => {
    return (
      <ToolsWrapper>
        {hasAccessToEdit ? (
          <ExtraToolsWrapper>
            <ToolBtn
              commandName={INSERT_DOC_TASK_SECTION_COMMAND}
              editor={state.editor}
              label="toolbar.add_task_section"
              haveItsOwnHandler
              command={state.editor?.commands.get(
                INSERT_DOC_TASK_SECTION_COMMAND,
              )}
              haveActiveState={checkActiveState(
                INSERT_DOC_TASK_SECTION_COMMAND,
              )}
              enabled={checkDefaultEnabled(INSERT_DOC_TASK_SECTION_COMMAND)}
            >
              <TaskSectionToolHandler editor={state.editor} />
            </ToolBtn>

            <ToolButtonWrapper onClick={openAttachment}>
              <Popover
                placement="top"
                content={
                  <div className={styles.tooltip__content}>
                    <p>{addAttachments}</p>
                  </div>
                }
                overlayClassName={`${styles.tooltip}`}
              >
                <Attachment20 />
              </Popover>
            </ToolButtonWrapper>
          </ExtraToolsWrapper>
        ) : null}

        {hasAccessToDelete ? (
          <ToolButtonWrapper onClick={onConfirm}>
            <Popover
              placement="top"
              content={
                <div className={styles.tooltip__content}>
                  <p>{deleteText}</p>
                </div>
              }
              overlayClassName={`${styles.tooltip}`}
            >
              <span data-cy="delete_status_update">
                <DeleteIcon />
              </span>
            </Popover>
          </ToolButtonWrapper>
        ) : null}
      </ToolsWrapper>
    );
  }, [
    addAttachments,
    deleteText,
    hasAccessToDelete,
    hasAccessToEdit,
    onConfirm,
    state.editor,
  ]);

  const hasAccessToFooter = useMemo(() => {
    return activeStatusUpdate?.private
      ? hasAccessToCreatePrivateUpdate
      : hasAccessToCreatePublicUpdate;
  }, [
    activeStatusUpdate?.private,
    hasAccessToCreatePrivateUpdate,
    hasAccessToCreatePublicUpdate,
  ]);
  const { data: spotlights } = useGetSpotlights({
    scopeId: currentSU?.statusUpdateId,
    scopeType: PinScopeType.StatusUpdate,
    resourceType: PinResourceType.Chat,
  });

  return (
    <Wrapper
      data-cy="status_update_editor"
      isFullScreen={handleFullScreen.active}
    >
      <div ref={toastContainer} className={styles.toast} />
      <CKMentionPopup
        renderOnPortal
        projectId={projectId}
        mentionData={mentionsData}
        editor={editor}
        attachTo={refMentionPopup.current}
        onUserMention={onUserMention}
        updateParentElementLocation={(position) => {
          setMentionPopupPosition({
            left: position.left,
            top: position.top,
          });
        }}
      >
        <div
          ref={refMentionPopup}
          style={{
            top: mentionPopupPosition.top,
            left: mentionPopupPosition.left,
          }}
          className={styles.mention_popup}
        />
      </CKMentionPopup>
      <CKVariablesPopup
        projectId={projectId}
        editor={editor}
        attachTo={refMentionPopup.current}
        updateParentElementLocation={(position) => {
          setMentionPopupPosition({
            left: position.left,
            top: position.top,
          });
        }}
      >
        <div
          ref={refMentionPopup}
          style={{
            top: mentionPopupPosition.top,
            left: mentionPopupPosition.left,
          }}
          className={styles.mention_popup}
        />
      </CKVariablesPopup>
      <AIFillSelectorPopover
        editor={editor}
        insertAsPlaceholder
        updateParentElementLocation={(position) => {
          setMentionPopupPosition({
            left: position.left,
            top: position.top,
          });
        }}
        template={false}
        allowMeetingSelector
        aiFillMeta={{ company: project?.customer }}
      >
        <div
          ref={refMentionPopup}
          style={{
            top: mentionPopupPosition.top,
            left: mentionPopupPosition.left,
          }}
          className={styles.mention_popup}
        />
      </AIFillSelectorPopover>
      <StatusEditorWrapper>
        {respMode !== true
          ? editorHeader ?? (
              <EditorHeadWrapper>
                <EditorHead
                  isAiFillResolving={isAiFillResolving}
                  onAsyncOperationSuccess={onAsyncOperationSuccess}
                  gridApi={gridApi as GridApi}
                  userEmails={userEmails}
                  setUserEmails={setUserEmails}
                  handleFullScreen={handleFullScreen}
                  canDelete={hasAccessToDelete}
                  canEdit={hasAccessToEdit}
                  projectId={projectId}
                  canCreate={hasAccessToPublish}
                  onPostDelete={onPostDelete}
                  spotlights={spotlights ?? []}
                  onSpotlightClick={onSpotlightClick}
                />
              </EditorHeadWrapper>
            )
          : null}
        <EditorContentWrapper>
          <div
            className={`${styles.editor}  ${
              currentSU?.state === StatusUpdateState.Published ||
              respMode ||
              !hasAccessToEdit
                ? styles.editor__readonly
                : ''
            }`}
          >
            <ErrorBoundary fallback={fallBack}>
              <div className={styles.presence} ref={presenceListElementRef} />
              {isAiFillResolving ? <AiFillPlaceholderSkeleton /> : null}
              {currentSU &&
              presenceListElementRef.current &&
              !isAiFillResolving ? (
                <CKCollabEditor
                  className="status-update__editor"
                  onEditorReady={onEditorReady}
                  singleUserMode={
                    currentSU?.state === StatusUpdateState.Published
                  }
                  readOnly={
                    currentSU?.state === StatusUpdateState.Published ||
                    !hasAccessToEdit
                  }
                  customClasses={{
                    editorClass: styles.ck_editor,
                  }}
                  extraPlugins={[
                    TaskSection,
                    ResourceListSection,
                    VariablesUI,
                    ProjectSummary,
                    ...aiFillPlugins,
                  ]}
                  extraConfig={{
                    aiFill: {
                      addAsPlaceholderOnly: false,
                      destination: AiFillAppDestination.ProjectUpdate,
                      meta: {
                        company: project?.customer,
                      },
                    },
                    ...taskSectionConfig({
                      publishedData: statusUpdateDetails?.publishedData ?? [],
                    }),
                    ...resourceListConfig({
                      publishedData: statusUpdateDetails?.publishedData ?? [],
                    }),
                    ...projectSummaryconfig(
                      {
                        publishedData: statusUpdateDetails?.publishedData ?? [],
                      },
                      undefined,
                    ),
                  }}
                  extraContextConfig={{
                    publishedData: statusUpdateDetails?.publishedData ?? [],
                  }}
                  presenceListElementRef={presenceListElementRef}
                  docId={currentSU.document.documentId}
                  projectId={projectId}
                  onEditorFocusChange={onEditorFocusChange}
                  shouldHidePlaceHolder={isPublished || !hasAccessToEdit}
                  isStatusUpdateEditor
                />
              ) : null}
            </ErrorBoundary>
          </div>
        </EditorContentWrapper>
        <MarginSpacer />

        {outsideUsers.length ? (
          <OutsideEmails>
            <FormattedMessage
              id="su.cc.modal.outside_users"
              values={{
                emails: outSideEmails.join(', '),
                emailsCount: outsideUsers.length,
                strong: (val) => <strong>{val}</strong>,
              }}
            />
          </OutsideEmails>
        ) : null}

        <EditorAttachementsWraper>
          <SUAttachment
            attachmentKey={attachmentKey}
            setAttachmentKey={setAttachmentKey}
            key={activeStatusUpdate?.statusUpdateId}
            trigger={trigger}
          />
        </EditorAttachementsWraper>

        {!isPublished || (isPublished && userEmails.length && !respMode) ? (
          <EditorFooterWrapper>
            <EditorFooterContentWrapper>
              {hasAccessToFooter && (
                <StatusUpdatePublishModal
                  onTabChange={onTabChange}
                  onBeforePublish={saveDocument}
                  onAsyncOperationSuccess={onAsyncOperationSuccess}
                  userEmails={userEmails}
                  setUserEmails={setUserEmails}
                  hasAccessToPublish={hasAccessToPublish}
                  projectId={projectId}
                />
              )}
              {!isPublished && hasAccessToPublish && hasAccessToFooter && (
                <FooterActionBar
                  editor={state.editor}
                  showDocTitle={false}
                  docTitle=""
                  tools={statusUpdateTools}
                  renderExtraFields={renderExtra}
                  topbarClassName={styles.topbar}
                  isResponsive={respMode}
                  hideTools={!hasAccessToEdit}
                />
              )}
            </EditorFooterContentWrapper>
          </EditorFooterWrapper>
        ) : null}
      </StatusEditorWrapper>
    </Wrapper>
  );
};
